import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { animate, style, transition, trigger, state } from '@angular/animations';
import { COLLAPSE_TIMING, EXPAND_TIMING, contentFade } from 'src/app/shared/animations/expand-fade.animation';

@Component({
  selector: 'dropdown-panel',
  templateUrl: './dropdown-panel.component.html',
  styleUrls: ['./dropdown-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // arrow animation
    trigger('toggleArrow', [
      state('collapsed', style({ transform: 'rotate(0)' })),
      state('expanded', style({ transform: 'rotate(-180deg)' })),
      transition('expanded => collapsed', animate(COLLAPSE_TIMING)),
      transition('collapsed => expanded', animate(EXPAND_TIMING))
    ]),
    contentFade,
  ]
})
export class DropdownPanelComponent implements OnInit {
  @Input() title: string = '';
  /** Optional muted note beside the title, e.g. an unread count. Hidden when empty. */
  @Input() titleNote: string = '';
  @Input() collapsedContent: TemplateRef<any> | null = null;
  @Input() borderless?: boolean = true;
  @Input() paddings?: string = 'none';

  @Input() disabled: boolean = false;
  @Input() hideHeader: boolean = false;
  @Input() initiallyExpanded: boolean = false;

  @Input() expand!: EventEmitter<void>;
  @Input() needToScrollBottomAfterExpand: boolean = false;

  /** Fires with the new expanded state whenever the panel opens or closes, so a projected
   *  widget can react to being collapsed (e.g. the Scenes widget leaves reorder mode). */
  @Output() toggled = new EventEmitter<boolean>();

  @ViewChild('anchor') scrollAnchor!: ElementRef<HTMLElement>;

  protected arrowState = 'collapsed';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** The preview wrapper is visible while the panel is CLOSED, so its state is inverted. */
  public get collapsedFadeState(): 'expanded' | 'collapsed' {
    return this.arrowState === 'collapsed' ? 'expanded' : 'collapsed';
  }

  public get expandedFadeState(): 'expanded' | 'collapsed' {
    return this.arrowState === 'expanded' ? 'expanded' : 'collapsed';
  }

  public ngOnInit(): void {
    if (this.initiallyExpanded) {
      this.arrowState = 'expanded';
    }

    if (this.expand) {
      this.expand.subscribe(() => this.setExpanded(true));
    }
  }

  public onToggle(): void {
    this.setExpanded(this.arrowState === 'collapsed');

    if (this.needToScrollBottomAfterExpand && this.arrowState === 'expanded') {
      setTimeout(() => {
        this.scrollAnchor.nativeElement.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
        // TODO: add check if scrolling is needed
        // const panelsWrapper = this.scrollAnchor.nativeElement.parentElement?.parentElement;
        // if (panelsWrapper && panelsWrapper.offsetHeight + panelsWrapper.offsetTop > window.innerHeight) {
        //   this.scrollAnchor.nativeElement.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
        // }
      }, 250)
    }
  }

  /** Single writer for arrowState so both entry points (the caret and the `expand` input)
   *  announce the change, and neither announces a state the panel is already in. */
  private setExpanded(expanded: boolean): void {
    const next = expanded ? 'expanded' : 'collapsed';
    if (this.arrowState === next) { return; }
    this.arrowState = next;
    // OnPush: the caret path is marked dirty by its event binding, but the `expand` input is an
    // external subscription — without this the arrow and the fade keep the old state.
    this.cdr.markForCheck();
    this.toggled.emit(expanded);
  }
}
